// Ultra-Modern Professional Corporate Design System 2025
export const Colors = {
  // Professional Primary Palette - Enterprise Grade
  primary: '#1a1a1a',           // Deep professional black
  primaryLight: '#2d2d2d',      // Lighter professional grey
  primaryDark: '#0a0a0a',       // Pure deep black
  secondary: '#4f46e5',         // Professional indigo (toned down)
  accent: '#3730a3',            // Deep professional blue
  accentSecondary: '#0369a1',   // Professional sky blue (toned down)
  tertiary: '#6d28d9',          // Professional purple (toned down)
  quaternary: '#047857',        // Professional forest green
  
  // Professional Status Colors - Maximum contrast for enterprise dashboards
  success: '#15803d',           // Dark forest green (WCAG AAA compliant)
  successDark: '#14532d',       // Very dark green for maximum contrast
  warning: '#a16207',           // Dark golden amber (WCAG AAA compliant)  
  warningDark: '#854d0e',       // Very dark amber for emphasis
  danger: '#991b1b',            // Dark crimson (WCAG AAA compliant)
  dangerDark: '#7f1d1d',        // Very dark crimson for emphasis
  info: '#1e40af',              // Dark royal blue (WCAG AAA compliant)
  infoDark: '#1e3a8a',          // Very dark blue for emphasis
  
  // Text on colored backgrounds
  textOnSuccess: '#ffffff',
  textOnDanger: '#ffffff',
  textOnWarning: '#ffffff',
  textOnInfo: '#ffffff',
  
  // Ultra-Clean Surface System
  background: '#ffffff',                   // Pure white background
  backgroundLight: '#fafafa',              // Very light background
  backgroundDark: '#fafafa',               // Subtle off-white
  backgroundSecondary: '#f8fafc',          // Ultra-light grey
  backgroundTertiary: '#f1f5f9',           // Subtle background variant
  surface: '#ffffff',                      // Pure white surface
  surfaceElevated: '#ffffff',              // Elevated pure white
  surfaceFloating: '#ffffff',              // Floating white elements
  surfaceGlass: 'rgba(255, 255, 255, 0.98)', // Subtle glass effect
  surfaceHover: '#f8fafc',                 // Ultra-subtle hover
  surfacePressed: '#f1f5f9',               // Pressed state
  surfaceSelected: 'rgba(99, 102, 241, 0.04)', // Minimal selection
  surfaceAccent: 'rgba(99, 102, 241, 0.02)',   // Barely visible accent
  surfaceOverlay: 'rgba(0, 0, 0, 0.01)',      // Minimal overlay
  
  // Modal and overlay backgrounds
  overlayLight: 'rgba(0, 0, 0, 0.3)',
  overlayMedium: 'rgba(0, 0, 0, 0.5)',
  overlayDark: 'rgba(0, 0, 0, 0.7)',
  overlayHeavy: 'rgba(0, 0, 0, 0.85)',
  
  // Professional Border System
  border: '#e2e8f0',                       // Clean professional border
  borderLight: '#f1f5f9',                  // Ultra-light border
  borderDark: '#cbd5e1',                   // Defined professional border
  borderAccent: 'rgba(99, 102, 241, 0.1)', // Subtle professional accent
  borderFocus: '#6366f1',                  // Focus state border
  
  // Professional Text Hierarchy
  textPrimary: '#0f172a',       // Deep professional black
  textSecondary: '#334155',     // Professional dark grey
  textTertiary: '#64748b',      // Professional medium grey
  textQuaternary: '#94a3b8',    // Professional light grey
  textOnPrimary: '#ffffff',     // Pure white on dark
  textOnDark: '#f8fafc',        // Light text on dark
  textMuted: '#94a3b8',         // Muted professional text
  
  // Opacity utilities
  withOpacity: (color: string, opacity: number) => {
    // Convert hex to RGB if needed
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  },
  
  // Predefined opacity levels
  primaryOpacity10: 'rgba(26, 26, 26, 0.10)',
  primaryOpacity15: 'rgba(26, 26, 26, 0.15)',
  primaryOpacity20: 'rgba(26, 26, 26, 0.20)',
  secondaryOpacity10: 'rgba(99, 102, 241, 0.10)',
  secondaryOpacity15: 'rgba(99, 102, 241, 0.15)',
  secondaryOpacity20: 'rgba(99, 102, 241, 0.20)',
  
  // Professional Chart Colors - Enterprise Dashboard Palette
  chartColors: {
    blue: '#1e40af',      // Professional navy
    teal: '#0f766e',      // Professional teal
    green: '#15803d',     // Professional forest green
    yellow: '#a16207',    // Professional amber
    orange: '#c2410c',    // Professional burnt orange
    red: '#991b1b',       // Professional crimson
    purple: '#6d28d9',    // Professional purple
    pink: '#be185d',      // Professional rose
    indigo: '#4338ca',    // Professional indigo
    gray: '#52525b',      // Professional gray
  },
  
  // Professional Data Visualization Colors - Enterprise Suite
  dataViz: {
    primary: '#1e40af',     // Professional navy
    series1: '#0f766e',     // Professional teal
    series2: '#15803d',     // Professional forest green
    series3: '#a16207',     // Professional amber
    series4: '#c2410c',     // Professional burnt orange
    series5: '#991b1b',     // Professional crimson
    series6: '#6d28d9',     // Professional purple
    series7: '#be185d',     // Professional rose
  }
};

export const Spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
  xxxxl: 64,
};

export const FontSizes = {
  xs: 11,
  sm: 12,
  md: 14,
  lg: 16,
  xl: 18,
  xxl: 20,
  xxxl: 24,
  display: 28,
  hero: 32,
};

export const FontWeights = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const FontStacks = {
  primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  mono: '"SF Mono", "Monaco", "Consolas", "Liberation Mono", monospace',
  system: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

export const AIAnimations = {
  // Neural Network Animations
  neuralPulse: 'neuralPulse 2s ease-in-out infinite',
  synapseFlow: 'synapseFlow 3s ease-in-out infinite',
  cognitiveWave: 'cognitiveWave 4s ease-in-out infinite',
  // Ambient Interactions  
  ambientFloat: 'ambientFloat 6s ease-in-out infinite',
  intelligentHover: 'intelligentHover 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  contextualShift: 'contextualShift 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  // Quantum Easing
  quantumEase: 'cubic-bezier(0.16, 1, 0.3, 1)',
  neuralEase: 'cubic-bezier(0.25, 1, 0.5, 1)',
  ambientEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

export const NeuralGrid = {
  // AI-Native Grid System
  minTileSize: '280px',
  maxTileSize: '1fr',
  neuralGap: '24px',
  synapseGap: '32px',
  cognitiveGap: '40px',
  // Dynamic Breakpoints
  quantumBreak: '1920px',
  neuralBreak: '1440px',
  cognitiveBreak: '1024px',
  synapseBreak: '768px',
  ambientBreak: '480px',
};

export const BorderRadius = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  xxl: 16,
  round: 9999,
};

export const Shadows = {
  none: 'none',
  // Ultra-subtle Professional Shadows
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.02)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(0, 0, 0, 0.02)',
  md: '0 2px 4px -1px rgba(0, 0, 0, 0.04), 0 1px 3px -1px rgba(0, 0, 0, 0.02)',
  lg: '0 4px 6px -2px rgba(0, 0, 0, 0.04), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
  xl: '0 8px 16px -4px rgba(0, 0, 0, 0.04), 0 4px 8px -2px rgba(0, 0, 0, 0.02)',
  glass: '0 4px 16px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
  card: '0 1px 3px 0 rgba(0, 0, 0, 0.03), 0 1px 2px 0 rgba(0, 0, 0, 0.02)',
  // Neural Network Shadows
  neuron: '0 2px 8px rgba(79, 70, 229, 0.15), 0 1px 3px rgba(79, 70, 229, 0.1)',
  synapse: '0 4px 16px rgba(139, 92, 246, 0.12), 0 2px 8px rgba(139, 92, 246, 0.08)',
  cognitive: '0 8px 32px rgba(79, 70, 229, 0.1), 0 4px 16px rgba(139, 92, 246, 0.08)',
  // AI-Native Depth System
  ambient: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  floating: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1)',
  elevated: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)',
  levitated: '0 16px 48px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.06)',
  orbital: '0 24px 64px rgba(0, 0, 0, 0.08), 0 12px 32px rgba(0, 0, 0, 0.04)',
  // Dynamic Glows
  aiGlow: '0 0 32px rgba(79, 70, 229, 0.4), 0 0 16px rgba(139, 92, 246, 0.3)',
  smartGlow: '0 0 24px rgba(6, 182, 212, 0.3), 0 0 12px rgba(79, 70, 229, 0.2)',
  contextGlow: '0 0 20px rgba(139, 92, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  // Glass Morphism Enhanced
  neuralGlass: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(79, 70, 229, 0.1)',
  quantumGlass: '0 16px 48px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(139, 92, 246, 0.15)',
  // Modal Shadows
  modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
};

export const Breakpoints = {
  xs: '375px',    // Small mobile
  sm: '640px',    // Mobile
  md: '768px',    // Tablet
  lg: '1024px',   // Desktop
  xl: '1280px',   // Large desktop
  xxl: '1536px',  // Extra large
  
  // Legacy support
  mobile: '768px',
  tablet: '1024px',
  desktop: '1200px',
  wide: '1600px',
};

export const PowerBIAnimations = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
  easing: {
    standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
    accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
  },
};

// Layout Constants for consistent spacing and header handling
export const Layout = {
  headerHeight: 64,           // PowerBI header minimum height
  headerHeightPx: '64px',     // String version for CSS
  subHeaderHeight: 48,        // Secondary header/tab height
  subHeaderHeightPx: '48px',  // String version for CSS
  
  // Total offsets for proper content positioning
  contentOffsetWithHeader: 64 + 24,        // Header + padding
  contentOffsetWithSubHeader: 64 + 48 + 24, // Header + sub-header + padding
  
  // Z-index hierarchy
  zIndex: {
    background: 0,
    content: 1,
    sidebar: 50,
    header: 100,
    dropdown: 1000,
    modal: 2000,
    tooltip: 3000,
  },
};